import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ExternalLink, LoaderCircle, Search } from 'lucide-react';

import { AppTopHeader } from '@/components/AppTopHeader';
import { HighlightedSelections, highlightMarkClassName } from '@/components/HighlightedSelections';
import { VideoPlayerWithControls } from '@/components/videos/VideoPlayerWithControls';
import { Button } from '@/components/ui/button';
import { useRecentVideoSearches } from '@/hooks/useRecentVideoSearches';
import { useVideoSearch } from '@/hooks/useVideoSearch';
import { useYoutubeOEmbed, youtubeFallbackThumbnailUrl } from '@/hooks/useYoutubeOEmbed';
import type { YoutubeSearchItem } from '@/services';
import { cn } from '@/services/utils';

import { RecentSearchesPanel, YourWordsPanel } from './VideosSidebar';
import { MEANING_GRID_COLS_CLASS, MEANING_ROW_MAX_WIDTH_CLASS } from '@/pages/meaning/layoutGrid';

// Matches Tailwind's `md` breakpoint. The mobile and desktop layouts below each mount their own
// SynchronizedYoutubePlayer, so only one of the two trees may ever be in the DOM at once — a CSS-only
// `md:hidden` / `hidden md:flex` pair still mounts BOTH players (one just invisible), and the hidden
// one keeps playing its audio, which is why pausing the visible player didn't stop the sound.
const DESKTOP_LAYOUT_QUERY = '(min-width: 768px)';

function useIsDesktopLayout() {
  const [isDesktopLayout, setIsDesktopLayout] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(DESKTOP_LAYOUT_QUERY).matches
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(DESKTOP_LAYOUT_QUERY);
    const updateMatch = () => setIsDesktopLayout(mediaQuery.matches);

    updateMatch();
    mediaQuery.addEventListener('change', updateMatch);
    return () => mediaQuery.removeEventListener('change', updateMatch);
  }, []);

  return isDesktopLayout;
}

const formatTimestamp = (milliseconds: number) => {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const externalYoutubeUrl = (item: YoutubeSearchItem) =>
  `https://www.youtube.com/watch?v=${encodeURIComponent(item.videoId)}&t=${Math.max(0, Math.floor(item.playbackStartMilliseconds / 1000))}s`;

const toSelections = (item: YoutubeSearchItem) =>
  (item.highlightSpans ?? [])
    .map((span) => ({ start: span.start, length: span.end - span.start }))
    .filter((selection) => selection.length > 0);

/** The ribbon of loaded-but-not-playing results under the player (design bundle screen 14b:
 * "first clip plays, the rest as a ribbon under it"). */
function VideoResultRow({
  item,
  active,
  onSelect,
}: {
  item: YoutubeSearchItem;
  active: boolean;
  onSelect: () => void;
}) {
  const { metadata } = useYoutubeOEmbed(item.videoId);
  const caption = metadata ? [metadata.title, metadata.channel].filter(Boolean).join(' · ') : '';

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={active ? 'true' : undefined}
      className={cn(
        'flex w-full items-center gap-3 rounded-xl p-2 text-left transition',
        active ? 'bg-[#e7f0e8]' : 'hover:bg-[#f6f4ef]'
      )}
    >
      <span className="relative aspect-video w-[84px] shrink-0 overflow-hidden rounded-lg bg-slate-200">
        <img
          src={youtubeFallbackThumbnailUrl(item.videoId)}
          alt=""
          loading="lazy"
          onError={(event) => event.currentTarget.classList.add('invisible')}
          className="size-full object-cover"
        />
        <span className="absolute bottom-1 right-1 rounded bg-slate-950/70 px-1 text-[9px] font-semibold tabular-nums text-white">
          {formatTimestamp(item.playbackStartMilliseconds)}
        </span>
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] italic text-[#3d3a33]">
          &ldquo;
          <HighlightedSelections
            text={item.sentence}
            selections={toSelections(item)}
            renderHighlight={(highlighted) => (
              <mark className={cn(highlightMarkClassName, 'not-italic')}>{highlighted}</mark>
            )}
          />
          &rdquo;
        </span>
        {caption && <span className="mt-0.5 block truncate text-xs text-[#a39c8d]">{caption}</span>}
      </span>
    </button>
  );
}

export default function VideosPage() {
  const [searchParams] = useSearchParams();
  const { key: locationKey } = useLocation();
  const urlQuery = searchParams.get('q')?.trim() ?? '';
  const [searchInput, setSearchInput] = useState(urlQuery);
  const {
    submittedQuery,
    items,
    activeItem,
    activeIndex,
    matchCount,
    hasMore,
    canMoveNext,
    loading,
    loadingMore,
    error,
    loadMoreError,
    captions,
    captionsLoading,
    captionsError,
    search,
    goToNext,
    goToPrevious,
    goToIndex,
  } = useVideoSearch();
  const { recentSearches, addSearch } = useRecentVideoSearches();
  const isDesktopLayout = useIsDesktopLayout();
  const { metadata: activeMetadata } = useYoutubeOEmbed(activeItem?.videoId);

  const runSearch = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setSearchInput(trimmed);
    void search(trimmed);
    addSearch(trimmed);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    runSearch(searchInput);
  };

  useEffect(() => {
    if (urlQuery) {
      runSearch(urlQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationKey]);

  if (isDesktopLayout) {
    return (
      <div className="flex min-h-dvh flex-col bg-[#f6f4ef]">
        <AppTopHeader />
        {/* Same column grid as /meaning (see layoutGrid.ts), so "Your words" lines up under the
            logo at exactly the same offset and width as This week there. Main spans the middle
            and right tracks — /videos has no third column of its own. */}
        <div className={cn('mx-auto grid w-full flex-1 items-start', MEANING_GRID_COLS_CLASS, MEANING_ROW_MAX_WIDTH_CLASS)}>
          {/* p-4 pt-8, px-9 pt-12 below: matches /training's sidebar/main top padding (not
              /meaning's p-4 / px-[18px] pt-[18px]) — /training is the closer sibling here. */}
          <aside className="sticky top-[66px] flex min-w-0 max-h-[calc(100dvh-66px)] flex-col gap-3.5 self-start overflow-y-auto p-4 pt-8">
            <YourWordsPanel selectedWord={submittedQuery} onSelect={runSearch} />
            <RecentSearchesPanel searches={recentSearches} onSelect={runSearch} />
          </aside>

          <main className="col-span-2 flex min-w-0 flex-1 flex-col gap-5 px-9 pb-24 pt-12">
            {(() => {
              // Empty state (design bundle screen 16a): heading and copy sit above a search box
              // capped to their own width. Once a query is submitted, the heading goes away and the
              // same box becomes a persistent full-width search bar at the top instead.
              const isEmptyState = !submittedQuery && !loading;
              const searchBar = (
                <form
                  onSubmit={handleSubmit}
                  role="search"
                  className={cn(
                    'flex items-center gap-2 rounded-full border-[1.5px] border-[#2f5c4e] bg-white py-1.5 pl-5 pr-1.5 shadow-[0_0_0_3px_rgba(47,92,78,0.14)]',
                    isEmptyState && 'w-full max-w-[680px]'
                  )}
                >
                  <Search className="size-[17px] shrink-0 text-[#2f5c4e]" />
                  <label htmlFor="video-search-desktop" className="sr-only">Search video transcripts</label>
                  <input
                    id="video-search-desktop"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="Word or phrase"
                    required
                    maxLength={200}
                    autoComplete="off"
                    className="h-9 min-w-0 flex-1 bg-transparent text-[15px] text-[#1e1c18] outline-none placeholder:text-[#a39c8d]"
                  />
                  <Button
                    type="submit"
                    disabled={loading || !searchInput.trim()}
                    className="h-9 shrink-0 rounded-full bg-[#0f1115] px-5 text-[13px] font-bold text-white hover:bg-black"
                  >
                    {loading ? <LoaderCircle className="size-4 animate-spin" /> : 'Find clips'}
                  </Button>
                </form>
              );

              if (!isEmptyState) return searchBar;

              return (
                // No flex-1/justify-center/vertical padding here: /training deliberately sits its
                // own heading at a fixed offset from the top rather than centering it in the
                // remaining space (centering made the gap balloon on tall screens) — same reasoning
                // applies here, so this sits right after main's own pt-12, matching /training's
                // "How should we ask you" heading exactly instead of floating lower.
                <div className="flex flex-col gap-6">
                  <div className="flex max-w-[680px] flex-col gap-4">
                    <h2 className="font-display text-[32px] font-semibold leading-[1.15] text-[#1e1c18]">
                      Hear a phrase the way people really say it
                    </h2>
                    <p className="text-[14.5px] leading-6 text-[#6b6659]">
                      Type a word or phrase — yawca goes through podcasts, interviews and shorts and brings back the
                      moments where it is actually spoken. Nothing is searched until you ask.
                    </p>
                  </div>
                  {searchBar}
                </div>
              );
            })()}

            {loading && (
              <div className="flex items-center justify-center gap-2 rounded-2xl border border-[#e6e2da] bg-white py-10 text-sm text-[#57534a]">
                <LoaderCircle className="size-5 animate-spin" />
                Searching video transcripts...
              </div>
            )}

            {!loading && error && (
              <div role="alert" className="flex flex-col gap-3 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between">
                <span>{error}</span>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void search(submittedQuery)}
                  className="h-8 shrink-0 rounded-full border-red-200 bg-white text-red-700 hover:bg-red-100"
                >
                  Try again
                </Button>
              </div>
            )}

            {!loading && submittedQuery && !error && matchCount === 0 && (
              <div className="rounded-2xl border border-dashed border-[#e6e2da] bg-white/70 py-10 text-center">
                <p className="font-semibold text-[#1e1c18]">No matches for “{submittedQuery}”</p>
                <p className="mt-1 text-sm text-[#57534a]">Try a shorter phrase or a different grammatical form.</p>
              </div>
            )}

            {!loading && activeItem && (
              <div className="flex flex-col gap-4">
                <p className="text-[13px] font-semibold text-[#57534a]">
                  {matchCount}{hasMore ? '+' : ''} clip{matchCount === 1 && !hasMore ? '' : 's'} with this phrase · playing {activeIndex + 1} of {matchCount}{hasMore ? '+' : ''}
                </p>

                <VideoPlayerWithControls
                  videoId={activeItem.videoId}
                  playbackStartMilliseconds={activeItem.playbackStartMilliseconds}
                  sentenceId={activeItem.sentenceId}
                  highlightSpans={activeItem.highlightSpans ?? []}
                  captions={captions}
                  captionsLoading={captionsLoading}
                  captionsError={captionsError}
                  activeIndex={activeIndex}
                  canMoveNext={canMoveNext}
                  loadingMore={loadingMore}
                  loadMoreError={loadMoreError}
                  onPrevious={goToPrevious}
                  onNext={() => void goToNext()}
                  overlay={
                    activeMetadata && (
                      <span className="pointer-events-none absolute left-2.5 top-2.5 rounded-full bg-slate-950/70 px-2.5 py-1 text-xs font-bold text-white">
                        {[activeMetadata.title, activeMetadata.channel].filter(Boolean).join(' · ')}
                      </span>
                    )
                  }
                />

                <div className="flex justify-end">
                  <a
                    href={externalYoutubeUrl(activeItem)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-[#a39c8d] hover:text-[#2f5c4e] hover:underline"
                  >
                    Open on YouTube
                    <ExternalLink className="size-3" />
                  </a>
                </div>

                {items.length > 1 && (
                  <div className="flex flex-col gap-1 border-t border-[#ece9e2] pt-3">
                    {items.map((item, index) => (
                      index !== activeIndex && (
                        <VideoResultRow
                          key={item.sentenceId}
                          item={item}
                          active={false}
                          onSelect={() => goToIndex(index)}
                        />
                      )
                    ))}
                  </div>
                )}
              </div>
            )}
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f4ef]">
      <main className="min-h-full px-4 pb-[calc(6.5rem+env(safe-area-inset-bottom))] pt-[max(1.5rem,env(safe-area-inset-top))] sm:px-6">
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
          {!submittedQuery && !loading && (
            <div className="flex flex-col gap-3">
              <h1 className="font-display text-[26px] font-semibold leading-[1.15] text-[#1e1c18]">
                Hear a phrase the way people really say it
              </h1>
              <p className="text-[14.5px] leading-6 text-[#6b6659]">
                Type a word or phrase — yawca goes through podcasts, interviews and shorts and brings back the
                moments where it is actually spoken. Nothing is searched until you ask.
              </p>
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            role="search"
            className="flex items-center gap-2 rounded-full border-[1.5px] border-[#2f5c4e] bg-white py-1.5 pl-5 pr-1.5 shadow-[0_0_0_3px_rgba(47,92,78,0.14)]"
          >
            <Search className="size-[17px] shrink-0 text-[#2f5c4e]" />
            <label htmlFor="video-search" className="sr-only">Search video transcripts</label>
            <input
              id="video-search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Word or phrase"
              required
              maxLength={200}
              autoComplete="off"
              className="h-9 min-w-0 flex-1 bg-transparent text-[15px] text-[#1e1c18] outline-none placeholder:text-[#a39c8d]"
            />
            <Button
              type="submit"
              disabled={loading || !searchInput.trim()}
              className="h-9 shrink-0 rounded-full bg-[#0f1115] px-5 text-[13px] font-bold text-white hover:bg-black"
            >
              {loading ? <LoaderCircle className="size-4 animate-spin" /> : 'Find clips'}
            </Button>
          </form>

          {loading && (
            <div className="flex items-center justify-center gap-2 rounded-2xl border border-[#e6e2da] bg-white py-10 text-sm text-[#57534a]">
              <LoaderCircle className="size-5 animate-spin" />
              Searching video transcripts...
            </div>
          )}

          {!loading && error && (
            <div role="alert" className="flex flex-col gap-3 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between">
              <span>{error}</span>
              <Button
                type="button"
                variant="outline"
                onClick={() => void search(submittedQuery)}
                className="h-8 shrink-0 rounded-full border-red-200 bg-white text-red-700 hover:bg-red-100"
              >
                Try again
              </Button>
            </div>
          )}

          {!loading && submittedQuery && !error && matchCount === 0 && (
            <div className="rounded-2xl border border-dashed border-[#e6e2da] bg-white/70 py-10 text-center">
              <p className="font-semibold text-[#1e1c18]">No matches for “{submittedQuery}”</p>
              <p className="mt-1 text-sm text-[#57534a]">Try a shorter phrase or a different grammatical form.</p>
            </div>
          )}

          {!loading && activeItem && (
            <div className="flex flex-col gap-4">
              <p className="text-[13px] font-semibold text-[#57534a]">
                {matchCount}{hasMore ? '+' : ''} clip{matchCount === 1 && !hasMore ? '' : 's'} with this phrase · playing {activeIndex + 1} of {matchCount}{hasMore ? '+' : ''}
              </p>

              <VideoPlayerWithControls
                videoId={activeItem.videoId}
                playbackStartMilliseconds={activeItem.playbackStartMilliseconds}
                sentenceId={activeItem.sentenceId}
                highlightSpans={activeItem.highlightSpans ?? []}
                captions={captions}
                captionsLoading={captionsLoading}
                captionsError={captionsError}
                activeIndex={activeIndex}
                canMoveNext={canMoveNext}
                loadingMore={loadingMore}
                loadMoreError={loadMoreError}
                onPrevious={goToPrevious}
                onNext={() => void goToNext()}
                overlay={
                  activeMetadata && (
                    <span className="pointer-events-none absolute left-2.5 top-2.5 rounded-full bg-slate-950/70 px-2.5 py-1 text-xs font-bold text-white">
                      {[activeMetadata.title, activeMetadata.channel].filter(Boolean).join(' · ')}
                    </span>
                  )
                }
              />

              <div className="flex justify-end">
                <a
                  href={externalYoutubeUrl(activeItem)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium text-[#a39c8d] hover:text-[#2f5c4e] hover:underline"
                >
                  Open on YouTube
                  <ExternalLink className="size-3" />
                </a>
              </div>

              {items.length > 1 && (
                <div className="flex flex-col gap-1 border-t border-[#ece9e2] pt-3">
                  {items.map((item, index) => (
                    index !== activeIndex && (
                      <VideoResultRow
                        key={item.sentenceId}
                        item={item}
                        active={false}
                        onSelect={() => goToIndex(index)}
                      />
                    )
                  ))}
                </div>
              )}
            </div>
          )}

          {!submittedQuery && !loading && (
            <div className="flex flex-col gap-4">
              <YourWordsPanel selectedWord={submittedQuery} onSelect={runSearch} />
              <RecentSearchesPanel searches={recentSearches} onSelect={runSearch} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

